'use client';

import { transcribeAudioRoute } from '@/app/api/chat/transcribe/route.def';
import { Kbd } from '@/components/common/Kbd';
import { callApi } from '@/domains/common/api-client';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

type Phase = 'recording' | 'transcribing' | 'sending' | 'error';

interface Props {
  onClose: () => void;
}

export const VoiceCapture: FunctionComponent<Props> = ({ onClose }) => {
  const [phase, setPhase] = useState<Phase>('recording');
  const [seconds, setSeconds] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void start();
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        cleanup();
        onClose();
      } else if (e.key === ' ' && phase === 'recording') {
        e.preventDefault();
        stop();
      } else if (e.key === 'Enter' && phase === 'recording') {
        e.preventDefault();
        stop();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase]);

  function cleanup(): void {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  async function start(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mime = pickMimeType();
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = e => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    setSeconds(0);
    tickRef.current = window.setInterval(() => setSeconds(s => s + 1), 1000);
  }

  function stop(): void {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorder.onstop = (): void => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      void process(blob);
    };
    recorder.stop();
  }

  async function process(blob: Blob): Promise<void> {
    setPhase('transcribing');
    const ext = (recorderRef.current?.mimeType ?? 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
    const file = new File([blob], `voice.${ext}`, { type: blob.type });
    const form = new FormData();
    form.append('file', file);
    const data = await callApi(transcribeAudioRoute, {
      body: form,
      gracefulFallback: () => {
        setErrorMsg('Something went wrong');
        setPhase('error');
      },
    });
    const transcript = (data.text ?? '').trim();
    if (!transcript) {
      setErrorMsg('Nothing was transcribed. Try again.');
      setPhase('error');
      return;
    }
    setPhase('sending');
    window.dispatchEvent(new CustomEvent('matilda:voice', { detail: { transcript } }));
    window.setTimeout(() => onClose(), 200);
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex animate-[cmdk-fade_0.12s_ease-out] items-center justify-center bg-[rgba(15,17,21,0.5)] p-6 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[380px] animate-[cmdk-pop_0.14s_ease-out] flex-col overflow-hidden rounded-[14px] border border-line-2 bg-bg-2 shadow-[0_24px_60px_rgba(0,0,0,0.24),0_2px_6px_rgba(0,0,0,0.06)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-ink-3">
            Recording for Matilda
          </span>
          <button
            className="cursor-pointer border-none bg-transparent text-[22px] leading-none text-ink-4 hover:text-ink"
            onClick={onClose}
            aria-label="Cancel recording"
          >
            ×
          </button>
        </div>

        {phase === 'recording' ? (
          <div className="flex flex-col items-center gap-3 px-[22px] pb-[22px] pt-[26px]">
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#d94545] text-white shadow-[0_4px_14px_rgba(217,69,69,0.35)]">
              <span className="absolute -inset-1.5 animate-[vc-pulse_1.4s_ease-out_infinite] rounded-full border-2 border-[rgba(217,69,69,0.45)]" />
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
            </div>
            <div className="font-mono text-[22px] font-medium tabular-nums text-ink">
              {formatTime(seconds)}
            </div>
            <div className="text-center text-[12.5px] text-ink-3">Recording — speak your instruction</div>
            <button
              className="mt-1 cursor-pointer rounded-sm border-none bg-ink px-4 py-2 font-sans text-[12.5px] font-medium text-white transition-colors duration-150 hover:bg-[#1c1f25]"
              onClick={stop}
            >
              Stop & send
            </button>
            <div className="mt-1 flex gap-3.5 text-[10.5px] text-ink-4">
              <span className="inline-flex items-center gap-[5px]">
                <Kbd>space</Kbd> stop
              </span>
              <span className="inline-flex items-center gap-[5px]">
                <Kbd>esc</Kbd> cancel
              </span>
            </div>
          </div>
        ) : phase === 'transcribing' ? (
          <div className="flex flex-col items-center gap-3 px-[22px] pb-[22px] pt-[26px]">
            <div className="h-7 w-7 animate-[vc-spin_0.8s_linear_infinite] rounded-full border-[2.5px] border-line border-t-acc" />
            <div className="text-center text-[12.5px] text-ink-3">Transcribing…</div>
          </div>
        ) : phase === 'sending' ? (
          <div className="flex flex-col items-center gap-3 px-[22px] pb-[22px] pt-[26px]">
            <div className="h-7 w-7 animate-[vc-spin_0.8s_linear_infinite] rounded-full border-[2.5px] border-line border-t-acc" />
            <div className="text-center text-[12.5px] text-ink-3">Sending to Matilda…</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 px-[22px] pb-[22px] pt-[26px]">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-soft text-lg font-semibold text-rose">
              !
            </div>
            <div className="text-center text-[12.5px] text-ink-3">Couldn&apos;t record</div>
            <div className="max-w-[260px] text-center text-[11.5px] text-rose">{errorMsg}</div>
            <button
              className="mt-1 cursor-pointer rounded-sm border-none bg-ink px-4 py-2 font-sans text-[12.5px] font-medium text-white transition-colors duration-150 hover:bg-[#1c1f25]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function pickMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  return candidates.find(c => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c));
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
